import React, { useCallback, useEffect, useState, useRef } from "react";
import axios from "../../api";
import {
  Card,
  TextField,
  FormControl,
  CircularProgress,
  Grid,
  Switch,
  FormHelperText,
  FormControlLabel,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Radio,
  Box,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import Locations from "examples/ReuseFunctions/Locations";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  name: Yup.string().required("Name is required"),

  phone: Yup.string()
    .matches(/^\d{10}$/, "Enter a valid phone number")
    .required("Phone number is required"),

  email: Yup.string()
    .matches(
      /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/,
      "Invalid email format"
    )
    .typeError("Enter a valid email")
    .required("Email is required"),
  state_id: Yup.string().required("State is required"),
  city_id: Yup.string().required("City is required"),
  region_id: Yup.string().required("Region is required"),
  address_type: Yup.string().required("Address type is required"),
  address: Yup.string().required("Address is required"),
  company_pan: Yup.string().when("type", ([type], schema) => {
    return type === "company"
      ? schema.required("Pan is required")
      : schema.notRequired().nullable();
  }),
  company_gst: Yup.string().when("type", ([type], schema) => {
    return type === "company"
      ? schema.required("Gst is required")
      : schema.notRequired().nullable();
  }),
});

interface State {
  state_id: number;
  state_name: string;
}

interface City {
  city_id: number;
  city_name: string;
}

interface Region {
  region_id: number;
  region_name: string;
}

interface Customer {
  name: string;
  email: string;
  phone: string;
  is_priority: number;
  is_active: number;
  state_id: number;
  city_id: number;
  region_id: number;
  address_type: string;
  address: string;
  type?: string;
  address_id: number;
  company_pan?: string | null;
  company_gst?: number | null;
}
export interface EditCustomersProps {
  id?: string;
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditCustomer = ({ id: idProp, embedded, onSuccess, onCancel }: EditCustomersProps = {}) => {
  const { id: idFromParams } = useParams();
  const id = idProp ?? idFromParams;
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialState, setInitialState] = useState<State | null>(null);
  const [initialCity, setInitialCity] = useState<City | null>(null);
  const [initialRegion, setInitialRegion] = useState<Region | null>(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [pendingType, setPendingType] = useState<"customer" | "company" | null>(
    null
  );
  const [previousType, setPreviousType] = useState<
    "customer" | "company" | null
  >(null);
  const [submitAfterConfirm, setSubmitAfterConfirm] = useState(false);
  const submitFormRef = useRef<(() => void) | null>(null);
  const { fetchError, fetchSuccess } = useSnackbar();
  const { states, cities, areas, setSelectedState, setSelectedCity } =
    Locations();
  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/customer/${id}`);
      const { data } = response;
      setCustomer(data);
      setSelectedState(data?.state_id);
      setSelectedCity(data?.city_id);
      setInitialState({
        state_id: data?.state_id,
        state_name: data?.state_name,
      });
      setInitialCity({
        city_id: data?.city_id,
        city_name: data?.city_name,
      });
      setInitialRegion({
        region_id: data?.region_id,
        region_name: data?.region_name,
      });
      setLoading(false);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, [id, setSelectedState, setSelectedCity]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const handleTypeChange = (
    newType: "customer" | "company",
    currentType: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (currentType !== newType) {
      setPreviousType(currentType as "customer" | "company");
      setPendingType(newType);
      setOpenConfirm(true);
    }
  };

  const Wrapper: React.ElementType = embedded ? Box : Card;
  const wrapperProps = embedded
    ? { sx: { width: "100%" } as any }
    : { sx: { padding: "24px", width: "100%", borderRadius: "8px" } };

  const formContent = loading ? (
    <CircularProgress />
  ) : (
    <Wrapper {...wrapperProps}>
      {!embedded && (
        <SoftTypography variant="h5" sx={{ marginBottom: "18px" }}>
          Edit Customer
        </SoftTypography>
      )}
      <Formik
            enableReinitialize
            initialValues={{
              name: customer?.name || "",
              email: customer?.email || "",
              phone: customer?.phone || "",
              is_priority: customer?.is_priority === 1,
              is_active: customer?.is_active === 1,
              state_id: customer?.state_id || "",
              city_id: customer?.city_id || "",
              region_id: customer?.region_id || "",
              address_type: customer?.address_type || "",
              address: customer?.address || "",
              type: customer?.type || "customer",
              company_pan: customer?.company_pan || null,
              company_gst: customer?.company_gst || null,
            }}
            validationSchema={validationSchema}
            onSubmit={async (values) => {
              if (!submitAfterConfirm && values.type !== customer?.type) {
                // User clicked Update but type changed → show confirm dialog
                setPreviousType(customer?.type as "customer" | "company");
                setPendingType(values.type as "customer" | "company");
                setSubmitAfterConfirm(true);
                setOpenConfirm(true);
                return;
              }

              // Continue with API update because confirmed
              setSubmitAfterConfirm(false);

              // Separate address fields
              const {
                state_id,
                city_id,
                region_id,
                address_type,
                address,
                type,
                ...customerFields
              } = values;

              const addressData = {
                state_id,
                city_id,
                region_id,
                address_type,
                address,
              };
              try {
                // Update customer with full fields including type & division
                await axios.put(`/customer/${id}`, customerFields);
                await axios.put(
                  `/address/${customer?.address_id}`,
                  addressData
                );
                fetchSuccess("Customer updated successfully");
                if (embedded && onSuccess) onSuccess();
                else navigate("/customers-list");
              } catch (err) {
                if (err instanceof AxiosError && err.response) {
                  fetchError(err.response.data.error);
                }
              }
            }}
          >
            {({
              errors,
              touched,
              handleChange,
              setFieldValue,
              values,
              submitForm,
            }) => {
              submitFormRef.current = submitForm;

              return (
                <Form>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <Box
                          component="fieldset"
                          sx={{ display: "flex", gap: "20px", border: "none" }}
                        >
                          <FormControlLabel
                            control={
                              <Radio
                                name="type"
                                value="customer"
                                checked={values.type === "customer"}
                                onChange={() =>
                                  handleTypeChange(
                                    "customer",
                                    values.type,
                                    setFieldValue
                                  )
                                }
                              />
                            }
                            label="Customer"
                          />

                          <FormControlLabel
                            control={
                              <Radio
                                name="type"
                                value="company"
                                checked={values.type === "company"}
                                onChange={() =>
                                  handleTypeChange(
                                    "company",
                                    values.type,
                                    setFieldValue
                                  )
                                }
                              />
                            }
                            label="Organisation"
                          />
                        </Box>

                        {errors.type && touched.type && (
                          <FormHelperText error>{errors.type}</FormHelperText>
                        )}
                      </FormControl>
                    </Grid>

                    <Grid container spacing={2}>
                      {values?.type === "customer" ? (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <SoftTypography variant="caption" fontWeight="bold">
                              Name
                            </SoftTypography>
                            <TextField
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              variant="outlined"
                              fullWidth
                              error={!!errors.name && touched.name}
                              helperText={touched.name && errors.name}
                            />
                          </FormControl>
                        </Grid>
                      ) : (
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <SoftTypography variant="caption" fontWeight="bold">
                              Organisation Name
                            </SoftTypography>
                            <TextField
                              name="name"
                              value={values.name}
                              onChange={handleChange}
                              variant="outlined"
                              fullWidth
                              error={!!errors.name && touched.name}
                              helperText={touched.name && errors.name}
                            />
                          </FormControl>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Phone Number
                          </SoftTypography>
                          <TextField
                            name="phone"
                            value={values.phone}
                            inputProps={{ maxLength: 10 }}
                            onChange={handleChange}
                            variant="outlined"
                            fullWidth
                            error={!!errors.phone && touched.phone}
                            helperText={touched.phone && errors.phone}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Email
                          </SoftTypography>
                          <TextField
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            variant="outlined"
                            fullWidth
                            error={!!errors.email && touched.email}
                            helperText={touched.email && errors.email}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth>
                          <FormControlLabel
                            control={
                              <Switch
                                name="is_priority"
                                checked={values.is_priority}
                                onChange={(event) => {
                                  setFieldValue(
                                    "is_priority",
                                    event.target.checked
                                  );
                                }}
                                color="primary"
                              />
                            }
                            label={
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                              >
                                Premium
                              </SoftTypography>
                            }
                          />
                          {touched.is_priority && errors.is_priority && (
                            <FormHelperText error>
                              {errors.is_priority}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} sm={3}>
                        <FormControl fullWidth>
                          <FormControlLabel
                            control={
                              <Switch
                                name="is_active"
                                checked={values.is_active}
                                onChange={(event) => {
                                  setFieldValue(
                                    "is_active",
                                    event.target.checked
                                  );
                                }}
                                color="primary"
                              />
                            }
                            label={
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                              >
                                Status
                              </SoftTypography>
                            }
                          />
                          {touched.is_active && errors.is_active && (
                            <FormHelperText error>
                              {errors.is_active}
                            </FormHelperText>
                          )}
                        </FormControl>
                      </Grid>
                      {values?.type === "company" && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                              >
                                Company Pan
                              </SoftTypography>
                              <TextField
                                name="company_pan"
                                value={values.company_pan}
                                inputProps={{ maxLength: 10 }}
                                onChange={handleChange}
                                variant="outlined"
                                fullWidth
                                error={
                                  !!errors.company_pan && touched.company_pan
                                }
                                helperText={
                                  touched.company_pan && errors.company_pan
                                }
                              />
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                              <SoftTypography
                                variant="caption"
                                fontWeight="bold"
                              >
                                Company Gst
                              </SoftTypography>
                              <TextField
                                name="company_gst"
                                value={values.company_gst}
                                inputProps={{ maxLength: 10 }}
                                onChange={handleChange}
                                variant="outlined"
                                fullWidth
                                error={
                                  !!errors.company_gst && touched.company_gst
                                }
                                helperText={
                                  touched.company_gst && errors.company_gst
                                }
                              />
                            </FormControl>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            State
                          </SoftTypography>
                          <Autocomplete
                            id="state-autocomplete"
                            options={states ?? []}
                            getOptionLabel={(option) => option.state_name || ""}
                            value={values.state_id ? initialState : null}
                            onChange={(event, newValue) => {
                              setFieldValue("city_id", "");
                              setFieldValue("region_id", "");
                              setFieldValue(
                                "state_id",
                                newValue ? newValue.state_id : ""
                              );
                              setInitialState(newValue ?? null);
                              setSelectedState(
                                newValue ? newValue.state_id : null
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="state_id"
                                variant="outlined"
                                error={!!errors.state_id && touched.state_id}
                                helperText={touched.state_id && errors.state_id}
                                fullWidth
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            City
                          </SoftTypography>
                          <Autocomplete
                            id="city-autocomplete"
                            options={cities ?? []}
                            getOptionLabel={(option) => option.city_name || ""}
                            value={values.city_id ? initialCity : null}
                            onChange={(event, newValue) => {
                              setFieldValue("region_id", "");
                              setFieldValue(
                                "city_id",
                                newValue ? newValue.city_id : ""
                              );
                              setInitialCity(newValue ?? null);
                              setSelectedCity(
                                newValue ? newValue.city_id : null
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="city_id"
                                variant="outlined"
                                error={!!errors.city_id && touched.city_id}
                                helperText={touched.city_id && errors.city_id}
                                fullWidth
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Area
                          </SoftTypography>
                          <Autocomplete
                            id="region-autocomplete"
                            options={areas ?? []}
                            getOptionLabel={(option) =>
                              option.region_name || ""
                            }
                            value={values.region_id ? initialRegion : null}
                            onChange={(event, newValue) => {
                              setFieldValue(
                                "region_id",
                                newValue ? newValue.region_id : ""
                              );
                              setInitialRegion(newValue ?? null);
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                name="region_id"
                                variant="outlined"
                                error={!!errors.region_id && touched.region_id}
                                helperText={
                                  touched.region_id && errors.region_id
                                }
                                fullWidth
                              />
                            )}
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Address Type
                          </SoftTypography>
                          <TextField
                            name="address_type"
                            value={values.address_type}
                            onChange={handleChange}
                            variant="outlined"
                            fullWidth
                            error={
                              !!errors.address_type && touched.address_type
                            }
                            helperText={
                              touched.address_type && errors.address_type
                            }
                          />
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth>
                          <SoftTypography variant="caption" fontWeight="bold">
                            Address
                          </SoftTypography>
                          <TextField
                            name="address"
                            value={values.address}
                            onChange={handleChange}
                            variant="outlined"
                            fullWidth
                            error={!!errors.address && touched.address}
                            helperText={touched.address && errors.address}
                          />
                        </FormControl>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <Grid container spacing={2}>
                        <Grid item>
                          <SoftButton
                            className="success-btn"
                            variant="gradient"
                            color="success"
                            type="submit"
                          >
                            Update
                          </SoftButton>
                        </Grid>
                        <Grid item>
                          <SoftButton
                            className="cancel-btn"
                            variant="gradient"
                            color="warning"
                            onClick={() =>
                              embedded && onCancel ? onCancel() : navigate("/customers-list")
                            }
                          >
                            Cancel
                          </SoftButton>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Box>
                  <Dialog
                    open={openConfirm}
                    onClose={() => setOpenConfirm(false)}
                  >
                    <DialogTitle>Confirm Change</DialogTitle>
                    <DialogContent>
                      Are you sure you want to change from{" "}
                      <Box component="b">{previousType}</Box> to{" "}
                      <Box component="b">{pendingType}</Box>?
                    </DialogContent>

                    <DialogActions>
                      <Button
                        className="cancel-btn"
                        color="warning"
                        onClick={() => {
                          setOpenConfirm(false);
                          setPendingType(null);
                        }}
                      >
                        Cancel
                      </Button>

                      <Button
                        onClick={() => {
                          setFieldValue("type", pendingType);
                          setOpenConfirm(false);
                          if (submitAfterConfirm && submitFormRef.current) {
                            submitFormRef.current();
                          }
                        }}
                        color="primary"
                        variant="contained"
                      >
                        Yes, Change
                      </Button>
                    </DialogActions>
                  </Dialog>
                </Form>
              );
            }}
          </Formik>
    </Wrapper>
  );

  if (embedded) return formContent;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {formContent}
    </DashboardLayout>
  );
};

export default EditCustomer;
